'use client'

import { PasswordResetResult, useSetPassword } from '@/hooks/useSetPassword'
import Button from 'lib/components/Button/Button'
import { PasswordInputStandalone } from 'lib/components/Form'
import Header from 'lib/components/Header/Header'
import Link from 'lib/components/Link/Link'
import Notification from 'lib/components/Notification/Notification'
import { useState } from 'react'

interface NewPasswordProps {
  passwordResetToken: string
  onBack: () => void
  onSuccess: (details: PasswordResetResult) => void
}

export default function NewPassword({
  passwordResetToken,
  onBack,
  onSuccess,
}: NewPasswordProps) {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const { loading, error, setPassword } = useSetPassword()

  const passwordMismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading || passwordMismatch) return
    const result = await setPassword(passwordResetToken, newPassword)
    if (result) onSuccess(result)
  }

  return (
    <form onSubmit={handleSubmit}>
      <Header as="h2" className="type-body-3 mb-4">
        Create new password
      </Header>
      <p className="type-body-8 mb-4">Enter your new password below.</p>
      {error && (
        <Notification
          size="small"
          icon={{ icon: 'error' }}
          type="urgent"
          title={error}
          className="mb-4 !text-left"
        />
      )}
      <PasswordInputStandalone
        label="New password*"
        placeholder="Enter new password"
        name="newPassword"
        disabled={loading}
        onChange={(val) => setNewPassword(String(val ?? ''))}
        error={
          newPassword.length > 0 && newPassword.length < 6
            ? 'Password must be at least 6 characters'
            : undefined
        }
      />
      <PasswordInputStandalone
        label="Confirm password*"
        placeholder="Confirm new password"
        name="confirmPassword"
        className="mt-4"
        disabled={loading}
        onChange={(val) => setConfirmPassword(String(val ?? ''))}
        error={passwordMismatch ? 'Passwords do not match' : undefined}
      />
      <Button
        type="submit"
        className="!mt-4 w-full !type-body-11-bold md:!type-body-5"
        disabled={
          loading ||
          !newPassword ||
          newPassword.length < 6 ||
          !confirmPassword ||
          passwordMismatch
        }
      >
        {loading ? 'Setting password...' : 'Set Password'}
      </Button>
      <div className="mt-4 text-center">
        <Link
          href="#"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault()
            onBack()
          }}
          className="md:!text-xs"
        >
          Back
        </Link>
      </div>
    </form>
  )
}
